package kz.dentalcrm.app.screens.login

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import compose.icons.FeatherIcons
import compose.icons.feathericons.LogIn
import compose.icons.feathericons.Shield
import dentalcrm.composeapp.generated.resources.Res
import dentalcrm.composeapp.generated.resources.email
import dentalcrm.composeapp.generated.resources.error_login
import dentalcrm.composeapp.generated.resources.login
import dentalcrm.composeapp.generated.resources.login_sub_title
import dentalcrm.composeapp.generated.resources.login_title
import dentalcrm.composeapp.generated.resources.password
import kz.dentalcrm.app.core.ui.UiStateScaffold
import kz.dentalcrm.app.core.ui.components.UIButton
import kz.dentalcrm.app.core.ui.components.UIPasswordInput
import kz.dentalcrm.app.core.ui.components.UITextInput
import kz.dentalcrm.app.navigation.AppNavigator
import kz.dentalcrm.app.navigation.MainRoute
import kz.dentalcrm.app.screens.login.mvi.LoginViewEvent
import kz.dentalcrm.app.screens.login.mvi.LoginViewSideEffect
import kz.dentalcrm.app.theme.DefaultTheme
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun LoginScreen() {
    val navigator: AppNavigator = koinInject()
    val viewModel: LoginViewModel = koinViewModel()
    val state by viewModel.viewState.collectAsState()

    LaunchedEffect(viewModel.effect) {
        viewModel.effect.collect { effect ->
            when (effect) {
                LoginViewSideEffect.NavigateToMain -> navigator.navigateAndClearBackStack(MainRoute)
            }
        }
    }

    UiStateScaffold(
        modifier = Modifier.fillMaxSize(),
        content = {
            LoginContent(
                loading = state.isLoading,
                error = state.error,
                resetError = { viewModel.setEvent(LoginViewEvent.ResetError) },
                login = { email, password -> viewModel.setEvent(LoginViewEvent.Login(email, password)) }
            )
        }
    )
}

@Composable
fun LoginContent(
    loading: Boolean,
    error: String?,
    resetError: () -> Unit,
    login: (email: String, password: String) -> Unit,
) {
    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(
                brush = Brush.verticalGradient(
                    listOf(
                        Color(0xFFf6f6f6),
                        Color(0xFF020022),
                    )
                )
            ),
        contentAlignment = Alignment.Center
    ) {
        Column(
            modifier = Modifier
                .widthIn(min = 320.dp, max = 460.dp)
                .imePadding()
                .padding(24.dp)
                .background(
                    color = DefaultTheme.colors.surface,
                    shape = RoundedCornerShape(24.dp)
                ),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center,
        ) {

            Spacer(modifier = Modifier.height(24.dp))

            Text(
                textAlign = TextAlign.Start,
                text = stringResource(Res.string.login_title),
                style = DefaultTheme.typography.heading2,
                color = DefaultTheme.colors.onSurface
            )

            Text(
                textAlign = TextAlign.Start,
                text = stringResource(Res.string.login_sub_title),
                style = DefaultTheme.typography.bodyMediumRegular,
                color = DefaultTheme.colors.onSurface
            )

            Spacer(modifier = Modifier.height(56.dp))

            UITextInput(
                modifier = Modifier
                    .padding(horizontal = 16.dp)
                    .height(64.dp)
                    .fillMaxWidth(),
                leadingIcon = FeatherIcons.LogIn,
                errorText = error ?: stringResource(Res.string.error_login),
                isOnError = error.isNullOrEmpty().not(),
                trailingActionClear = false,
                label = stringResource(Res.string.email),
            ) {
                resetError()
                email = it
            }

            Spacer(modifier = Modifier.height(12.dp))

            UIPasswordInput(
                modifier = Modifier
                    .padding(horizontal = 16.dp)
                    .height(64.dp)
                    .fillMaxWidth(),
                leadingIcon = FeatherIcons.Shield,
                label = stringResource(Res.string.password),
            ) {
                resetError()
                password = it
            }

            Spacer(modifier = Modifier.height(36.dp))

            UIButton(
                modifier = Modifier
                    .padding(horizontal = 16.dp)
                    .height(56.dp)
                    .fillMaxWidth(),
                isLoading = loading,
                onClick = {
                    if (loading.not()) {
                        login(email, password)
                    }
                },
                text = stringResource(Res.string.login)
            )
            Spacer(modifier = Modifier.height(24.dp))
        }
    }
}
